import { useCallback } from 'react';
import {
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE
} from '../../global/constants';
import { getWorkspaceProjectPreferenceData, updateWorkspaceProjectPreference } from '../../helpers/firebaseHelper';
import { AccountingProps, CurrentProject } from '../../interfaces/ProjectInterface';

type AccountingTaskFilter = NonNullable<CurrentProject>['taskFilterType'] | undefined;

interface UseAccountingTaskFiltersProps {
  currentProject: CurrentProject;
  onCloseCompletedDropdown: () => void;
  onCloseGroupMenu: () => void;
  onCloseOrderDropdown: () => void;
  params: { id: string };
  props: AccountingProps;
  taskFilter: AccountingTaskFilter;
}

export const useAccountingTaskFilters = ({
  currentProject,
  onCloseCompletedDropdown,
  onCloseGroupMenu,
  onCloseOrderDropdown,
  params,
  props,
  taskFilter
}: UseAccountingTaskFiltersProps) => {
  // We write with merge: true, so only the changed keys under tasks_filter_type are sent and the
  // other keys stay as they are. So we don't need to read the old value first — each toggle is one
  // write, no read.
  //
  // Note (Task 31): every toggle here only SETS a key (true/false or a number). None of them
  // delete a key. That's important because merge can add or change a key, but it can't remove one.
  // If you ever need to actually delete a key, don't rely on merge — use deleteField() with a
  // dotted path, e.g. { 'tasks_filter_type.order': deleteField() }. Otherwise the old value stays.
  const updateTaskPreference = useCallback(
    async (nextFilter: Record<string, unknown>) => {
      await updateWorkspaceProjectPreference(params.id, { tasks_filter_type: nextFilter });
    },
    [params.id]
  );

  const onChangeTaskListType = useCallback(
    async (listType: number) => {
      // Read the current order from the local store instead of the preference document — they
      // mirror each other, so no read is needed to decide whether the view switch changes order.
      const currentOrder = taskFilter?.orderByStatus;
      const order =
        currentOrder === SHOW_TASK_BY_ORDER.DUE_DATE && listType === TASK_LIST_TYPE.CALENDAR_VIEW
          ? SHOW_TASK_BY_ORDER.PRIORITY
          : currentOrder === SHOW_TASK_BY_ORDER.CUSTOM && listType === TASK_LIST_TYPE.CALENDAR_VIEW
          ? SHOW_TASK_BY_ORDER.CREATED_DATE
          : currentOrder || SHOW_TASK_BY_ORDER.CREATED_DATE;
      await updateWorkspaceProjectPreference(params.id, { tasks_filter_type: { order, viewType: listType } });
      currentProject?.updateTaskFilterType('taskViewType', listType || TASK_LIST_TYPE.TASKS);
      currentProject?.updateTaskFilterType('orderByStatus', order >= 0 ? order : SHOW_TASK_BY_ORDER.DUE_DATE);
      props.loadTaskList?.();
    },
    [currentProject, params.id, props, taskFilter]
  );

  // In-memory mutation helpers: update the local store and return the preference patch
  // WITHOUT touching Firestore. This lets onChangeGroupingFilter fold several changes into a
  // single merged write instead of chaining read+write per change.
  const applyCompletedTaskRecency = useCallback(
    (value: boolean): Record<string, unknown> => {
      currentProject?.updateTaskFilterType('showRecentlyCompletedTask', value);
      return { showRecentlyCompletedTask: value };
    },
    [currentProject]
  );

  const applyCompletedTaskViewDate = useCallback(
    (key: number | string): Record<string, unknown> => {
      const completedFilter = Number(key);
      currentProject?.updateTaskFilterType('showCompletedTaskByFilter', completedFilter);
      return { showCompletedTask: completedFilter };
    },
    [currentProject]
  );

  const handleTaskCompletedByRecency = useCallback(
    async (value: boolean) => {
      await updateTaskPreference(applyCompletedTaskRecency(value));
      props.loadTaskList?.();
    },
    [applyCompletedTaskRecency, props, updateTaskPreference]
  );

  const onChangeCompletedTaskViewDate = useCallback(
    async (key: number | string) => {
      const nextFilter = applyCompletedTaskViewDate(key);
      onCloseCompletedDropdown();
      await updateTaskPreference(nextFilter);
      props.loadTaskList?.();
    },
    [applyCompletedTaskViewDate, onCloseCompletedDropdown, props, updateTaskPreference]
  );

  const onChangeGroupingFilter = useCallback(
    async (key: string | number) => {
      const groupingStatus = Number(key);
      const projectFilter = (await getWorkspaceProjectPreferenceData(params.id, 'tasks_filter_type')) || {};
      const isCustomGrouping =
        groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP ||
        groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS;
      const order = isCustomGrouping
        ? SHOW_TASK_BY_ORDER.CUSTOM
        : projectFilter?.order === SHOW_TASK_BY_ORDER.CUSTOM
        ? SHOW_TASK_BY_ORDER.CREATED_DATE
        : projectFilter?.order || SHOW_TASK_BY_ORDER.CREATED_DATE;
      onCloseGroupMenu();
      currentProject?.updateTaskFilterType('orderByStatus', order);
      currentProject?.updateTaskFilterType(
        'groupingStatus',
        groupingStatus >= 0 ? groupingStatus : SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP
      );

      // Compute every preference change for this interaction in memory, then issue ONE merged
      // write. Previously the GROUPING_BY_STATUS branch chained handleTaskCompletedByRecency()
      // and onChangeCompletedTaskViewDate(), each doing its own read+write — turning a single
      // grouping change into 3 reads + 3 writes.
      const nextFilter: Record<string, unknown> = { grouping: groupingStatus, order };
      const isGroupingByStatus = groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS;
      if (isGroupingByStatus) {
        Object.assign(nextFilter, applyCompletedTaskRecency(true));
        onCloseCompletedDropdown();
        Object.assign(nextFilter, applyCompletedTaskViewDate(SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH));
      }

      await updateWorkspaceProjectPreference(params.id, {
        tasks_filter_type: { ...projectFilter, ...nextFilter }
      });

      if (isGroupingByStatus) props.loadTaskList?.();
      props.otherTaskList?.();
    },
    [
      applyCompletedTaskRecency,
      applyCompletedTaskViewDate,
      currentProject,
      onCloseCompletedDropdown,
      onCloseGroupMenu,
      params.id,
      props
    ]
  );

  const onChangeOrderByFilter = useCallback(
    async (key: string | number) => {
      const order = Number(key);
      await updateTaskPreference({ order });
      onCloseOrderDropdown();
      currentProject?.updateTaskFilterType('orderByStatus', order >= 0 ? order : SHOW_TASK_BY_ORDER.DUE_DATE);
      props.otherTaskList?.();
      if (taskFilter?.taskViewType === TASK_LIST_TYPE.CALENDAR_VIEW) props.loadWeekWiseTasks?.();
    },
    [currentProject, onCloseOrderDropdown, props, taskFilter, updateTaskPreference]
  );

  const toggleTaskFilter = useCallback(
    async (filterName: 'showSubTask' | 'showEmptyGroup') => {
      const nextValue = !taskFilter?.[filterName];
      currentProject?.updateTaskFilterType(filterName, nextValue);
      await updateTaskPreference({ [filterName]: nextValue });
      if (filterName === 'showSubTask') props.otherTaskList?.();
    },
    [currentProject, props, taskFilter, updateTaskPreference]
  );

  return {
    handleTaskCompletedByRecency,
    onChangeCompletedTaskViewDate,
    onChangeGroupingFilter,
    onChangeOrderByFilter,
    onChangeTaskListType,
    toggleTaskFilter
  };
};
